import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, X } from "lucide-react";

const NotificationPopup = ({ notification, hideNotification }) => {
    if (!notification) return null;

    const getNotificationStyles = () => {
        const baseStyles = "fixed top-6 right-6 z-50 max-w-md p-4 rounded-lg shadow-lg border transition-all duration-300 ease-in-out";
        
        switch (notification.type) {
            case 'success':
                return `${baseStyles} bg-green-50 border-green-200 text-green-800`;
            case 'error':
                return `${baseStyles} bg-red-50 border-red-200 text-red-800`;
            case 'info':
                return `${baseStyles} bg-blue-50 border-blue-200 text-blue-800`;
            default:
                return `${baseStyles} bg-gray-50 border-gray-200 text-gray-800`;
        }
    };

    const getIcon = () => {
        switch (notification.type) {
            case 'success':
                return <CheckCircle className="w-5 h-5 text-green-600" />;
            case 'error':
                return <AlertCircle className="w-5 h-5 text-red-600" />;
            case 'info':
                return <AlertCircle className="w-5 h-5 text-blue-600" />;
            default:
                return <AlertCircle className="w-5 h-5 text-gray-600" />;
        }
    };

    return (
        <div className={getNotificationStyles()}>
            <div className="flex items-start">
                <div className="mr-3 mt-0.5">
                    {getIcon()}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium break-words">
                        {notification.message}
                    </p>
                </div>
                <button
                    onClick={hideNotification}
                    className="ml-3 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

const PortfolioModal = ({ portfolio, onClose, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    short_description: '',
    full_description: '',
    category: '',
    client_name: '',
    project_date: '',
    project_url: '',
    github_url: '',
    technologies: [],
    featured_image: '',
    gallery_images: '',
    featured: false,
    status: 'published'
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [customTech, setCustomTech] = useState('');
  const [notification, setNotification] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

  // Show notification function
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  // Hide notification function
  const hideNotification = () => {
    setNotification(null);
  };

  // Load portfolio data when editing
  useEffect(() => {
    if (portfolio) {
      loadPortfolioData(portfolio.id);
    } else {
      // Reset form for new portfolio
      setFormData({
        title: '',
        slug: '',
        short_description: '',
        full_description: '',
        category: '',
        client_name: '',
        project_date: '',
        project_url: '',
        github_url: '',
        technologies: [],
        featured_image: '',
        gallery_images: '',
        featured: false,
        status: 'published'
      });
      setSlugManuallyEdited(false);
      setCustomTech('');
    }
  }, [portfolio]);

  const loadPortfolioData = async (portfolioId) => {
    try {
      const response = await fetch(`/api/portfolio.php?id=${portfolioId}`);
      const result = await response.json();
      
      if (result.success) {
        const portfolioData = result.data;
        setFormData({
          title: portfolioData.title || '',
          slug: portfolioData.slug || '',
          short_description: portfolioData.short_description || '',
          full_description: portfolioData.full_description || '',
          category: portfolioData.category || '',
          client_name: portfolioData.client_name || '',
          project_date: portfolioData.project_date || '',
          project_url: portfolioData.project_url || '',
          github_url: portfolioData.github_url || '',
          technologies: Array.isArray(portfolioData.technologies) 
            ? portfolioData.technologies 
            : [],
          featured_image: portfolioData.featured_image || '',
          gallery_images: Array.isArray(portfolioData.gallery_images)
            ? portfolioData.gallery_images.join(', ')
            : (portfolioData.gallery_images || ''),
          featured: Boolean(portfolioData.featured),
          status: portfolioData.status || 'published'
        });
        setSlugManuallyEdited(true);
      } else {
        setError('Failed to load portfolio data: ' + result.error);
      }
    } catch (err) {
      console.error('Error loading portfolio data:', err);
      setError('Failed to load portfolio data');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate slug from title if not manually edited
    if (name === 'title' && !slugManuallyEdited) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }

    // Mark slug as manually edited if user changes it
    if (name === 'slug') {
      setSlugManuallyEdited(true);
    }

    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setIsUploading(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await fetch('https://cdn.kgcinnovations.com/upload.php', {
        method: 'POST',
        body: uploadFormData,
      });

      const result = await response.json();

      if (result.success) {
        setFormData(prev => ({
          ...prev,
          featured_image: result.data.url
        }));
        showNotification('Image uploaded successfully!', 'success');
      } else {
        showNotification('Error uploading image: ' + result.error, 'error');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      showNotification('Failed to upload image. Please try again.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setError('');

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        technologies: formData.technologies, // Already an array
        gallery_images: formData.gallery_images
          .split(',')
          .map(img => img.trim())
          .filter(img => img),
        featured: formData.featured ? 1 : 0
      };

      const url = portfolio 
        ? `/api/portfolio.php?id=${portfolio.id}`
        : '/api/portfolio.php';
      
      const method = portfolio ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();
      
      if (result.success) {
        setShowSuccessOverlay(true);
        // Wait 2 seconds before closing the modal
        setTimeout(() => {
          setShowSuccessOverlay(false);
          onSaveSuccess();
        }, 2000);
      } else {
        setError('Error saving portfolio: ' + (result.error || 'Unknown error'));
      }
      
    } catch (err) {
      console.error('Error saving portfolio:', err);
      setError('Failed to save portfolio. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const addTechnology = (tech) => {
    if (tech && !formData.technologies.includes(tech)) {
      setFormData(prev => ({
        ...prev,
        technologies: [...prev.technologies, tech]
      }));
    }
  };

  const removeTechnology = (techToRemove) => {
    setFormData(prev => ({
      ...prev,
      technologies: prev.technologies.filter(tech => tech !== techToRemove)
    }));
  };

  const handleCustomTechAdd = () => {
    if (customTech.trim()) {
      addTechnology(customTech.trim());
      setCustomTech('');
    }
  };

  const handleCustomTechKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomTechAdd();
    }
  };

  const categories = [
    { value: '', label: 'Select Category' },
    { value: 'web-development', label: 'Web Development' },
    { value: 'mobile-app', label: 'Mobile App' },
    { value: 'e-commerce', label: 'E-commerce' },
    { value: 'branding', label: 'Branding' },
    { value: 'ui-ux', label: 'UI/UX Design' },
    { value: 'other', label: 'Other' }
  ];

  const predefinedTechnologies = [
    'React', 'Vue.js', 'Angular', 'Next.js', 'Nuxt.js',
    'JavaScript', 'TypeScript', 'HTML5', 'CSS3', 'SCSS', 'Tailwind CSS',
    'Node.js', 'Express.js', 'PHP', 'Laravel', 'WordPress', 'Python', 'Django',
    'MySQL', 'PostgreSQL', 'MongoDB', 'Firebase', 'SQLite',
    'AWS', 'Google Cloud', 'Docker', 'Kubernetes',
    'Git', 'GitHub', 'GitLab', 'CI/CD',
    'React Native', 'Flutter', 'Swift', 'Kotlin',
    'Figma', 'Adobe XD', 'Photoshop', 'Illustrator',
    'RESTful API', 'GraphQL', 'WebSocket',
    'Jest', 'Cypress', 'Unit Testing',
    'Stripe', 'PayPal', 'E-commerce'
  ];

  return (
    <>
      <NotificationPopup 
        notification={notification} 
        hideNotification={hideNotification} 
      />
      
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white mb-20">
          {/* Success Overlay */}
          {showSuccessOverlay && (
            <div className="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center z-10 rounded-md">
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {portfolio ? 'Portfolio Updated!' : 'Portfolio Created!'}
                </h3>
                <p className="text-gray-600">
                  {portfolio ? 'Your portfolio item has been successfully updated.' : 'Your portfolio item has been successfully created.'}
                </p>
              </div>
            </div>
          )}
          {/* Modal Header */}
          <div className="flex justify-between items-center pb-3 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              {portfolio ? 'Edit Portfolio Item' : 'Add New Portfolio Item'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Modal Body */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Title */}
              <div className="md:col-span-2">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter portfolio title"
                />
              </div>

              {/* Slug */}
              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
                  Slug
                </label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="url-friendly-slug"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate from title</p>
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Client Name */}
              <div>
                <label htmlFor="client_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Client Name
                </label>
                <input
                  type="text"
                  id="client_name"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Client or company name"
                />
              </div>

              {/* Project Date */}
              <div>
                <label htmlFor="project_date" className="block text-sm font-medium text-gray-700 mb-1">
                  Project Date
                </label>
                <input
                  type="date"
                  id="project_date"
                  name="project_date"
                  value={formData.project_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Short Description */}
              <div className="md:col-span-2">
                <label htmlFor="short_description" className="block text-sm font-medium text-gray-700 mb-1">
                  Short Description
                </label>
                <textarea
                  id="short_description"
                  name="short_description"
                  value={formData.short_description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Brief description for listings"
                />
              </div>

              {/* Full Description */}
              <div className="md:col-span-2">
                <label htmlFor="full_description" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Description
                </label>
                <textarea
                  id="full_description"
                  name="full_description"
                  value={formData.full_description}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Detailed project description"
                />
              </div>

              {/* Project URL */}
              <div>
                <label htmlFor="project_url" className="block text-sm font-medium text-gray-700 mb-1">
                  Project URL
                </label>
                <input
                  type="url"
                  id="project_url"
                  name="project_url"
                  value={formData.project_url}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com"
                />
              </div>

              {/* GitHub URL */}
              <div>
                <label htmlFor="github_url" className="block text-sm font-medium text-gray-700 mb-1">
                  GitHub URL
                </label>
                <input
                  type="url"
                  id="github_url"
                  name="github_url"
                  value={formData.github_url}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://github.com/username/repo"
                />
              </div>

              {/* Technologies */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Technologies
                </label>
                
                {/* Selected Technologies */}
                <div className="mb-3">
                  <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-2 border border-gray-300 rounded-md bg-gray-50">
                    {formData.technologies.length > 0 ? (
                      formData.technologies.map((tech, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                        >
                          {tech}
                          <button
                            type="button"
                            onClick={() => removeTechnology(tech)}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 text-sm">No technologies selected</span>
                    )}
                  </div>
                </div>

                {/* Predefined Technologies */}
                <div className="mb-3">
                  <p className="text-xs text-gray-600 mb-2">Click to add:</p>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded-md bg-white">
                    {predefinedTechnologies
                      .filter(tech => !formData.technologies.includes(tech))
                      .map((tech, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => addTechnology(tech)}
                          className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          + {tech}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Custom Technology Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customTech}
                    onChange={(e) => setCustomTech(e.target.value)}
                    onKeyPress={handleCustomTechKeyPress}
                    placeholder="Add custom technology..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleCustomTechAdd}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Featured Image */}
              <div>
                <label htmlFor="featured_image" className="block text-sm font-medium text-gray-700 mb-1">
                  Featured Image
                </label>
                <div className="space-y-2">
                  <input
                    type="url"
                    id="featured_image"
                    name="featured_image"
                    value={formData.featured_image}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com/image.jpg"
                  />
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">or</span>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleImageUpload(file);
                        }}
                        className="hidden"
                        disabled={isUploading}
                      />
                      <span className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        {isUploading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Upload Image
                          </>
                        )}
                      </span>
                    </label>
                  </div>
                  {formData.featured_image && (
                    <div className="mt-2">
                      <img 
                        src={formData.featured_image} 
                        alt="Preview" 
                        className="h-20 w-20 object-cover rounded-md border border-gray-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Gallery Images */}
              <div className="md:col-span-2">
                <label htmlFor="gallery_images" className="block text-sm font-medium text-gray-700 mb-1">
                  Gallery Images
                </label>
                <textarea
                  id="gallery_images"
                  name="gallery_images"
                  value={formData.gallery_images}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/img1.jpg, https://example.com/img2.jpg (comma-separated URLs)"
                />
              </div>

              {/* Featured Checkbox */}
              <div className="md:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                    Featured Project
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">Featured projects appear prominently on the website</p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 mt-8 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  portfolio ? 'Update Portfolio' : 'Create Portfolio'
                )}
              </button>
            </div>
          </div>
        </div>
    </>
  );
};

export default PortfolioModal;